(() => {
  class AkSeriesGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      queueMicrotask(() => {
        if (signal.aborted) return;
        this.frames = [...this.querySelectorAll('.ak-series-frame')];
        this.card = this.closest('.ak-series-card');
        this.position = 0;
        this.target = 0;
        this.frame = 0;
        this.lastTime = 0;
        this.hovered = false;
        this.ready = false;
        this.loading = null;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        if (!this.card || this.frames.length < 2) return;
        this.paint();
        const move = event => {
          if (event.pointerType === 'touch') return;
          const rect = this.card.getBoundingClientRect();
          if (!rect.width) return;
          this.hovered = true;
          const progress = Math.max(0, Math.min(1, ((event.clientX - rect.left) / rect.width - .12) / .76));
          this.request(Math.round(progress * (this.frames.length - 1)));
        };
        this.card.addEventListener('pointerenter', move, { signal, passive: true });
        this.card.addEventListener('pointermove', move, { signal, passive: true });
        const leave = () => {
          this.hovered = false;
          this.request(0);
        };
        this.card.addEventListener('pointerleave', leave, { signal });
        this.card.addEventListener('pointercancel', leave, { signal });
        this.card.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const step = event.key === 'ArrowRight' ? 1 : -1;
          this.request(Math.round(this.target) + step);
        }, { signal });
        this.card.addEventListener('focusout', event => {
          if (!this.hovered && !this.card.contains(event.relatedTarget)) this.request(0);
        }, { signal });
        this.motion.addEventListener('change', () => this.start(), { signal });
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.reset();
        }, { signal });
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(entries => {
            if (!entries[0].isIntersecting) this.reset();
          });
          this.observer.observe(this.card);
        }
      });
    }
    async prepare() {
      if (this.ready) return true;
      if (!this.loading) {
        this.loading = Promise.all(this.frames.map(frame => {
          const image = frame.querySelector('img');
          image.loading = 'eager';
          if (image.complete && image.naturalWidth) return true;
          return image.decode().then(() => true, () => image.complete && image.naturalWidth > 0);
        })).then(results => {
          this.ready = results.every(Boolean);
          if (!this.ready) this.loading = null;
          return this.ready;
        });
      }
      return this.loading;
    }
    request(position) {
      this.target = Math.max(0, Math.min(this.frames.length - 1, position));
      if (this.ready) { this.start(); return; }
      const signal = this.controller.signal;
      this.prepare().then(ready => {
        if (ready && !signal.aborted) this.start();
      });
    }
    start() {
      if (!this.ready || !this.isConnected) return;
      if (this.motion.matches) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.position = this.target;
        this.paint();
        return;
      }
      if (!this.frame) this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      this.frame = 0;
      if (!this.isConnected || this.controller.signal.aborted) return;
      const dt = Math.min(50, this.lastTime ? time - this.lastTime : 16.67);
      this.lastTime = time;
      this.position += (this.target - this.position) * (1 - Math.exp(-dt / 145));
      if (Math.abs(this.target - this.position) < .001) this.position = this.target;
      this.paint();
      if (this.position !== this.target) this.frame = requestAnimationFrame(next => this.tick(next));
      else this.lastTime = 0;
    }
    paint() {
      const lower = Math.floor(this.position);
      const upper = Math.min(this.frames.length - 1, lower + 1);
      const blend = this.position - lower;
      this.frames.forEach((frame, index) => {
        const opacity = index === lower ? 1 : index === upper ? blend : 0;
        frame.style.opacity = String(opacity);
        frame.style.zIndex = index === lower ? '1' : index === upper ? '2' : '0';
        frame.classList.toggle('is-current', index === Math.round(this.position));
      });
    }
    reset() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.lastTime = 0;
      this.target = this.position = 0;
      this.hovered = false;
      this.paint();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }
  if (!customElements.get('ak-series-gallery')) customElements.define('ak-series-gallery', AkSeriesGallery);
})();
